import { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, ChevronDown, Send, Instagram, Music2, Facebook, Youtube } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { WHATSAPP_URL } from "./WhatsAppButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT_ROWS = [
  {
    icon: Mail,
    label: "Email",
    value: "croclab.valencia@gmail.com",
    href: "mailto:croclab.valencia@gmail.com",
    id: "email",
  },
  {
    icon: Phone,
    label: "WhatsApp",
    value: "+34 658 427 582",
    href: WHATSAPP_URL,
    external: true,
    id: "whatsapp",
  },
  {
    icon: MapPin,
    label: "Dirección",
    value: "C/ del Dr. Romagosa 10, Ciutat Vella, 46002 València",
    href: "https://maps.app.goo.gl/3ZcPNSGucypFjynt5",
    external: true,
    id: "direccion",
  },
];

const SOCIALS = [
  {
    icon: Instagram,
    handle: "@croclab.es",
    href: "https://www.instagram.com/croclab.es",
    id: "instagram",
  },
  {
    icon: Music2,
    handle: "@croc.lab",
    href: "https://www.tiktok.com/@croc.lab",
    id: "tiktok",
  },
  {
    icon: Facebook,
    handle: "CROC LAB",
    href: "https://www.facebook.com/share/1GiyxjfyjM/?mibextid=wwXIfr",
    id: "facebook",
  },
  {
    icon: Youtube,
    handle: "@CrocLabofficial",
    href: "https://www.youtube.com/@CrocLabofficial",
    id: "youtube",
  },
];

const initialForm = { name: "", email: "", topic: "Cliente", message: "" };

const Contacto = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Mensaje enviado", {
        description: "Gracias por escribirnos. Te responderemos muy pronto.",
      });
      setForm(initialForm);
    } catch (err) {
      toast.error("No se pudo enviar", {
        description: "Inténtalo de nuevo en unos segundos.",
      });
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    "w-full rounded-2xl border border-brand-charcoal/15 bg-white px-4 py-3 text-sm md:text-base text-brand-charcoal placeholder:text-brand-charcoal/40 focus:outline-none focus:ring-2 focus:ring-brand-pink/60 focus:border-brand-pink transition";

  return (
    <section id="contacto" data-testid="contacto-section" className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16">
        <Reveal>
          <p className="text-sm font-semibold uppercase tracking-widest text-brand-pink">Contacto</p>
          <h2 className="mt-3 font-display font-bold tracking-tight text-brand-charcoal text-3xl md:text-4xl">
            Hablemos
          </h2>
          <p className="mt-4 text-base md:text-lg text-brand-charcoal/75 leading-relaxed max-w-lg">
            ¿Eres cliente, quieres colaborar con nosotros o te interesa CROC LAB
            como oportunidad de negocio? Escríbenos desde el formulario y te
            responderemos muy pronto.
          </p>
          <span
            data-testid="contacto-apertura-badge"
            className="mt-6 inline-flex items-center gap-2 bg-brand-pink/10 text-brand-pink text-xs md:text-sm font-semibold rounded-full px-4 py-2 uppercase tracking-wide"
          >
            ¡Estamos abiertos!
          </span>
          <ul className="mt-10 space-y-5">
            {CONTACT_ROWS.map((row) => (
              <li key={row.id} data-testid={`contacto-info-${row.id}`} className="flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-brand-pink/10 text-brand-pink flex items-center justify-center shrink-0">
                  <row.icon className="w-5 h-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-brand-charcoal">{row.label}</p>
                  {row.href ? (
                    <a
                      data-testid={`contacto-link-${row.id}`}
                      href={row.href}
                      {...(row.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      className="text-sm text-brand-charcoal/60 hover:text-brand-pink transition-colors"
                    >
                      {row.value}
                    </a>
                  ) : (
                    <p className="text-sm text-brand-charcoal/60">{row.value}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-8">
            <p className="text-sm font-semibold uppercase tracking-widest text-brand-charcoal/50">
              Síguenos
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              {SOCIALS.map((social) => (
                <a
                  key={social.id}
                  data-testid={`contacto-social-${social.id}`}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${social.id} de CROC LAB (se abre en una pestaña nueva)`}
                  className="inline-flex items-center gap-2 bg-white border border-brand-charcoal/10 rounded-full px-4 py-2 text-sm font-semibold text-brand-charcoal hover:border-brand-pink hover:text-brand-pink transition-colors"
                >
                  <social.icon className="w-4 h-4 text-brand-pink" aria-hidden="true" />
                  {social.handle}
                </a>
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <form
            data-testid="contacto-form"
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl border border-brand-charcoal/10 shadow-sm p-6 md:p-10 space-y-5"
          >
            <div>
              <label htmlFor="contacto-nombre" className="block text-sm font-semibold text-brand-charcoal mb-2">
                Nombre
              </label>
              <input
                id="contacto-nombre"
                data-testid="contacto-input-nombre"
                type="text"
                required
                maxLength={120}
                placeholder="Tu nombre"
                value={form.name}
                onChange={update("name")}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="contacto-email" className="block text-sm font-semibold text-brand-charcoal mb-2">
                Email
              </label>
              <input
                id="contacto-email"
                data-testid="contacto-input-email"
                type="email"
                required
                maxLength={200}
                placeholder="tu@email.com"
                value={form.email}
                onChange={update("email")}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="contacto-tipo" className="block text-sm font-semibold text-brand-charcoal mb-2">
                Tipo de consulta
              </label>
              <div className="relative">
                <select
                  id="contacto-tipo"
                  data-testid="contacto-select-tipo"
                  value={form.topic}
                  onChange={update("topic")}
                  className={`${inputClass} appearance-none pr-10`}
                >
                  <option>Cliente</option>
                  <option>Colaboración</option>
                  <option>Negocio y expansión</option>
                  <option>Prensa</option>
                </select>
                <ChevronDown
                  className="w-4 h-4 text-brand-charcoal/50 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none"
                  aria-hidden="true"
                />
              </div>
            </div>
            <div>
              <label htmlFor="contacto-mensaje" className="block text-sm font-semibold text-brand-charcoal mb-2">
                Mensaje
              </label>
              <textarea
                id="contacto-mensaje"
                data-testid="contacto-input-mensaje"
                required
                maxLength={2000}
                rows={5}
                placeholder="Cuéntanos…"
                value={form.message}
                onChange={update("message")}
                className={`${inputClass} resize-none`}
              />
            </div>
            <div className="space-y-3">
              <label htmlFor="contacto-privacidad" className="flex items-start gap-3 cursor-pointer">
                <input
                  id="contacto-privacidad"
                  data-testid="contacto-checkbox-privacidad"
                  type="checkbox"
                  required
                  className="mt-1 w-4 h-4 shrink-0 accent-[#FF72AE]"
                />
                <span className="text-xs md:text-sm text-brand-charcoal/70 leading-relaxed">
                  He leído y acepto la{" "}
                  <a
                    href="/privacidad"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contacto-privacidad-link"
                    className="text-brand-pink font-semibold hover:underline"
                  >
                    Política de Privacidad
                  </a>
                  .
                </span>
              </label>
              <p className="text-xs text-brand-charcoal/50 leading-relaxed">
                Información básica sobre protección de datos — Responsable:
                CROC LAB, SOCIEDAD LIMITADA (NIF B88810940). Finalidad: atender
                tu consulta. Base jurídica: tu consentimiento. Destinatarios: no
                se ceden datos a terceros. Derechos: acceso, rectificación,
                supresión, oposición, limitación y portabilidad en
                croclab.valencia@gmail.com.
              </p>
            </div>
            <button
              data-testid="contacto-submit-button"
              type="submit"
              disabled={sending}
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-pink text-white font-semibold rounded-full px-8 py-3.5 hover:bg-brand-pink/90 hover:scale-[1.02] transition-transform disabled:opacity-60 disabled:hover:scale-100"
            >
              <Send className="w-4 h-4" aria-hidden="true" />
              {sending ? "Enviando…" : "Enviar mensaje"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contacto;
